import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BidQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../../utils";
import { getBidInfoForExternalUser } from "../public/getBidInfoForExternalUser";
import { useExternalBidQuery } from "../public/useExternalBidQuery";

const bidQuery = graphql(`
  query Bid($request: BidRequest!) {
    bid(BidRequest: $request) {
      id
      createdAt
      updatedAt
      procurementStageId
      opportunityId
      companyId
      teamId
      publicHash
      notice {
        guid
        name
        buyers {
          guid
          name
        }
      }
      opportunity {
        name
        stage {
          id
        }
      }
    }
  }
`);

export type BidInfo = BidQuery["bid"];

export function useBid(
  bidId: string,
  opportunityId?: string,
  options?: UseQueryOptions<BidQuery, unknown, BidQuery, QueryKey>,
) {
  const { urlShareSignature, isLoggedIn } = useExternalBidQuery();

  const { data, ...rest } = useGraphQlQuery(
    ["bid", bidId, opportunityId],
    bidQuery,
    [{ request: { id: bidId, urlShareSignature } }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: isLoggedIn,
    },
  );

  const { data: nonLoggedInData, ...other } = useQuery(
    ["bid", bidId, opportunityId],
    () => {
      if (urlShareSignature) {
        return getBidInfoForExternalUser(bidId, urlShareSignature);
      }
    },
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: !isLoggedIn,
    },
  );

  if (isLoggedIn) {
    return {
      data: data?.bid,
      ...rest,
    };
  } else {
    return {
      data: nonLoggedInData,
      ...other,
    };
  }
}
